import type { InjectedFormProps } from 'redux-form';
import { useDateFormat, useTranslation, ValidationProvider } from '@eti/providers';
import { css, mediaQueries } from '@eti/styles';
import type { ButtonVariantTypes } from '@etg/wings';
import { breakpoints, Button, Card, Heading, Inline, Stack, Text } from '@etg/wings';
import { Notification, variants } from '@eti/components';
import { isRequired, isRequiredIf } from '@eti/validation';
import { TripType as JourneyType } from '@eti/schema-types';
import Dates from '../../../widgets/search-form/containers/DatesContainer';
import {
  DEPARTURE_DATE,
  RETURN_DATE,
  SINGLE_BOUND,
} from '../../../widgets/search-form/constants/formFieldNames';
import type { DatesSearchFormProps, DatesSearchProps, TripType } from '../models';

const changeTripDatesSearchFormWrapperStyles = css`
  display: flex;
  width: 100%;
`;

const buttonStyles = css`
  align-self: end;
  display: flex;
  width: 100%;

  @media ${mediaQueries.large.up} {
    width: auto;
  }
`;

const isRequiredRule = isRequired({ message: 'Field is required' });
const isRequiredIfReturnRule = (originalTrip: TripType) => {
  const hasReturnDate = originalTrip.type
    ? (
        [
          JourneyType.Return,
          JourneyType.OpenJawDouble,
          JourneyType.OpenJawSingleDestination,
          JourneyType.OpenJawSingleOrigin,
        ] as JourneyType[]
      ).includes(originalTrip.type)
    : false;
  return isRequiredIf(() => hasReturnDate)({ message: 'Field is required' });
};

const setRules = (originalTrip: TripType) => ({
  [DEPARTURE_DATE]: isRequiredRule,
  [RETURN_DATE]: isRequiredIfReturnRule(originalTrip),
});

const DatesSearchForm = ({
  form,
  handleSubmit,
  setNewSearchDates,
  originalTrip,
}: DatesSearchProps & InjectedFormProps<DatesSearchFormProps, DatesSearchProps>) => {
  const { t } = useTranslation();
  const { date } = useDateFormat();

  const handleOnClick = ({ singleBound }: DatesSearchFormProps) => {
    const departureDate = singleBound.departureDate.getTime();
    const returnDate = singleBound.returnDate?.getTime() ?? null;
    setNewSearchDates({ departureDate, returnDate });
  };

  return (
    <ValidationProvider rules={setRules(originalTrip)}>
      <div className={changeTripDatesSearchFormWrapperStyles} id="changeTrip-search-form">
        <Card>
          <Stack spacing={16}>
            <Heading data-testid="changeTrip-changeDate-title" level={2}>
              {t('ChangeTrip.ChangeDate.Title')}
            </Heading>
            <Inline collapseBelow={breakpoints._768}>
              <Dates
                dateFormat={date}
                daysFromTodayForValidDepartureDate={0}
                departureDateName={`${SINGLE_BOUND}.${DEPARTURE_DATE}`}
                form={form}
                numberOfBounds={originalTrip.bounds.length}
                selectedTripType={originalTrip.type}
              />
              <Button
                className={buttonStyles}
                data-testid="changeTrip-changeDate-searchFlights-button"
                onClick={handleSubmit(handleOnClick)}
                variant={variants.PRIMARY as ButtonVariantTypes}
              >
                {t('Button.Text.Search.Air')}
              </Button>
            </Inline>
            <Notification
              dataTestId="changeTrip-changeDate-info-notification"
              variant={variants.INFORMATION}
            >
              <Text asHtml>{t('ChangeTrip.ChangeDate.Notification.Text')}</Text>
            </Notification>
          </Stack>
        </Card>
      </div>
    </ValidationProvider>
  );
};

export default DatesSearchForm;
